import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  FaArrowRight,
  FaCheck,
  FaCode,
  FaMobileAlt,
  FaWallet,
  FaBuilding,
  FaBullseye,
  FaEye,
  FaHandshake,
  FaLightbulb,
  FaExternalLinkAlt,
} from "react-icons/fa";

import Header from "../Templete/Header";
import Footer from "../Templete/Footer";
import "./AboutUs.css";

const serviceCategories = [
  {
    title: "Technology & Digital Solutions",
    icon: FaCode,
    description:
      "We help businesses turn their requirements into useful digital products. Our work brings together application development, design, and the technology needed to support everyday operations.",
    services: [
      "Software Development",
      "Mobile App Development",
      "Website Design & Development",
      "Graphics Design",
      "SEO Services",
      "UI/UX Design",
      "Technology Service Provider (TSP)",
    ],
  },
  {
    title: "Payments & E-Governance",
    icon: FaWallet,
    description:
      "We work on platforms and integrations that connect businesses and customers with payment and digital services, with attention to transaction journeys and operational requirements.",
    services: [
      "E-Governance Services",
      "Bill Payment Service",
      "Banking Business Correspondent Service",
      "Payment Services",
    ],
  },
];

const products = [
  {
    name: "Formax Pay",
    category: "PAYMENT TECHNOLOGY",
    icon: FaWallet,
    description:
      "Formax Pay brings our payment technology and business service capabilities into a dedicated platform. It supports our work on service integrations and infrastructure for business partners.",
    features: [
      "Business service integrations",
      "Payment technology infrastructure",
      "Partner-focused digital solutions",
    ],
    href: "https://formaxpay.com/",
    className: "fit-about__product--blue",
  },
];

const principles = [
  {
    icon: FaBullseye,
    title: "Understand the business first",
    description:
      "We start with the problem you need to solve, the people who will use the solution, and the way your business operates.",
  },
  {
    icon: FaLightbulb,
    title: "Keep solutions practical",
    description:
      "We focus on clear interfaces, useful features, and technology choices that fit the scope of the project.",
  },
  {
    icon: FaHandshake,
    title: "Work with clear communication",
    description:
      "We value agreed requirements, visible progress, and timely conversations about decisions and changes.",
  },
  {
    icon: FaCode,
    title: "Think beyond launch",
    description:
      "Maintainability, integration requirements, and future improvements form part of our approach to development.",
  },
];

const process = [
  {
    title: "Discover",
    description:
      "Understand your goals, users, existing systems, and the requirements that matter most.",
  },
  {
    title: "Plan & design",
    description:
      "Define the scope, map key user journeys, and establish the structure of the solution.",
  },
  {
    title: "Build & validate",
    description:
      "Develop the application or integration and check its behaviour against the agreed requirements.",
  },
  {
    title: "Launch & support",
    description:
      "Prepare for release and plan the support, maintenance, and improvements your project needs.",
  },
];

const technologyGroups = [
  {
    title: "Frontend",
    technologies: ["React", "JavaScript", "HTML", "CSS"],
  },
  {
    title: "Backend",
    technologies: ["Node.js", "PHP", "Java", "Python"],
  },
  {
    title: "Mobile",
    technologies: ["Flutter", "React Native", "Android"],
  },
  {
    title: "Databases",
    technologies: ["MySQL", "MongoDB"],
  },
];

const AboutUs = () => {
  useEffect(() => {
    document.title = "About Us | Formax IT Solutions Pvt. Ltd.";
  }, []);

  return (
    <>
      <Header />

      <main className="fit-about">
        {/* Introduction */}
        <section className="fit-about__hero">
          <div className="fit-about__container">
            <nav className="fit-about__breadcrumb" aria-label="Breadcrumb">
              <Link to="/">Home</Link>
              <span aria-hidden="true">/</span>
              <span aria-current="page">About Us</span>
            </nav>

            <div className="fit-about__hero-grid">
              <div>
                <span className="fit-about__eyebrow">ABOUT FORMAX IT</span>
                <h1>
                  Technology with purpose.
                  <span>Built around your business.</span>
                </h1>
                <p className="fit-about__hero-description">
                  We build digital solutions that connect businesses, simplify
                  operations, and make everyday services easier to access.
                </p>

                <div className="fit-about__hero-actions">
                  <Link to="/contact-us" className="fit-about__button">
                    Get in touch
                    <FaArrowRight aria-hidden="true" />
                  </Link>
                  <a href="#our-services" className="fit-about__text-link">
                    Explore our expertise
                    <FaArrowRight aria-hidden="true" />
                  </a>
                </div>
              </div>

              <aside
                className="fit-about__company-card"
                aria-label="Company overview"
              >
                <div className="fit-about__logo-panel">
                  <img
                    src="/images/logo.png"
                    alt="Formax IT Solutions"
                    width="210"
                    height="72"
                  />
                </div>

                <h2>Formax IT Solutions Pvt. Ltd.</h2>
                <p>
                  Software development, digital services, and payment technology
                  under one company.
                </p>

                <dl className="fit-about__company-facts">
                  <div>
                    <dt>Established</dt>
                    <dd>2018</dd>
                  </div>
                  <div>
                    <dt>Based in</dt>
                    <dd>Barasat, Kolkata</dd>
                  </div>
                  <div>
                    <dt>Our products</dt>
                    <dd>Formax Pay</dd>
                  </div>
                </dl>
              </aside>
            </div>
          </div>
        </section>

        {/* Company story */}
        <section className="fit-about__section">
          <div className="fit-about__container fit-about__story">
            <div className="fit-about__section-heading">
              <span className="fit-about__eyebrow">WHO WE ARE</span>
              <h2>A technology company with a practical outlook.</h2>
            </div>

            <div className="fit-about__story-copy">
              <p>
                Formax IT Solutions Private Limited is a technology company
                incorporated in 2018 and based in Barasat, Kolkata, West Bengal.
                Our work spans software development, mobile applications,
                websites, digital services, and payment technology.
              </p>
              <p>
                We believe a useful digital solution begins with a clear
                understanding of the business behind it. Whether the requirement
                is a customer-facing application, an internal system, or a
                service integration, we focus on how it will work in everyday
                use.
              </p>
              <p>
                Alongside our development services, we operate Formax Pay, our
                dedicated platform for payment technology and business services.
                We are building API infrastructure and payment orchestration
                capabilities that connect our engineering expertise with the
                operational needs of business partners.
              </p>
              <p>
                Our aim is to build lasting working relationships through clear
                communication, thoughtful implementation, and solutions that can
                evolve with business needs.
              </p>
            </div>
          </div>
        </section>

        {/* Mission and vision */}
        <section className="fit-about__section fit-about__section--soft">
          <div className="fit-about__container fit-about__purpose-grid">
            <article className="fit-about__purpose-card">
              <span className="fit-about__icon">
                <FaBullseye aria-hidden="true" />
              </span>
              <span className="fit-about__eyebrow">OUR MISSION</span>
              <h2>Make technology useful and accessible.</h2>
              <p>
                To help businesses adopt digital solutions that solve real
                problems, improve daily operations, and create simpler
                experiences for their customers and partners.
              </p>
            </article>

            <article className="fit-about__purpose-card">
              <span className="fit-about__icon fit-about__icon--green">
                <FaEye aria-hidden="true" />
              </span>
              <span className="fit-about__eyebrow">OUR VISION</span>
              <h2>Connect more businesses to digital opportunities.</h2>
              <p>
                To grow as a trusted technology partner by developing practical
                software, connected platforms, and digital services that help
                businesses move forward.
              </p>
            </article>
          </div>
        </section>

        {/* Services */}
        <section
          id="our-services"
          className="fit-about__section"
          aria-labelledby="about-services-title"
        >
          <div className="fit-about__container">
            <div className="fit-about__section-heading fit-about__section-heading--center">
              <span className="fit-about__eyebrow">OUR EXPERTISE</span>
              <h2 id="about-services-title">
                Two areas of expertise. One connected approach.
              </h2>
              <p>
                From building digital products to connecting everyday services,
                our work brings technology and business requirements together.
              </p>
            </div>

            <div className="fit-about__services-grid">
              {serviceCategories.map((category) => {
                const Icon = category.icon;

                return (
                  <article
                    className="fit-about__service-card"
                    key={category.title}
                  >
                    <span className="fit-about__icon">
                      <Icon aria-hidden="true" />
                    </span>
                    <h3>{category.title}</h3>
                    <p>{category.description}</p>

                    <ul className="fit-about__check-list">
                      {category.services.map((service) => (
                        <li key={service}>
                          <FaCheck aria-hidden="true" />
                          <span>{service}</span>
                        </li>
                      ))}
                    </ul>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {/* Products */}
        <section
          className="fit-about__section fit-about__section--dark"
          aria-labelledby="about-products-title"
        >
          <div className="fit-about__container">
            <div className="fit-about__section-heading">
              <span className="fit-about__eyebrow">OUR PRODUCTS</span>
              <h2 id="about-products-title">
                Our capabilities, brought to life.
              </h2>
              <p>
                Our product platforms extend our work into payment technology
                and everyday digital services.
              </p>
            </div>

            <div className="fit-about__products-grid">
              {products.map((product) => {
                const Icon = product.icon;

                return (
                  <article
                    key={product.name}
                    className={`fit-about__product ${product.className}`}
                  >
                    <div className="fit-about__product-heading">
                      <span className="fit-about__icon">
                        <Icon aria-hidden="true" />
                      </span>
                      <span className="fit-about__eyebrow">
                        {product.category}
                      </span>
                    </div>

                    <h3>{product.name}</h3>
                    <p>{product.description}</p>

                    <ul className="fit-about__check-list">
                      {product.features.map((feature) => (
                        <li key={feature}>
                          <FaCheck aria-hidden="true" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <a
                      href={product.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="fit-about__product-link"
                    >
                      Visit {product.name}
                      <FaExternalLinkAlt aria-hidden="true" />
                      <span className="fit-about__sr-only">
                        (opens in a new tab)
                      </span>
                    </a>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {/* Principles */}
        <section className="fit-about__section">
          <div className="fit-about__container">
            <div className="fit-about__section-heading fit-about__section-heading--center">
              <span className="fit-about__eyebrow">HOW WE THINK</span>
              <h2>The principles behind our work.</h2>
              <p>
                Good outcomes depend on more than code. They require
                understanding, collaboration, and attention to how a solution
                will be used.
              </p>
            </div>

            <div className="fit-about__principles-grid">
              {principles.map((principle) => {
                const Icon = principle.icon;

                return (
                  <article
                    className="fit-about__principle"
                    key={principle.title}
                  >
                    <span className="fit-about__icon">
                      <Icon aria-hidden="true" />
                    </span>
                    <h3>{principle.title}</h3>
                    <p>{principle.description}</p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="fit-about__section fit-about__section--soft">
          <div className="fit-about__container">
            <div className="fit-about__section-heading">
              <span className="fit-about__eyebrow">OUR APPROACH</span>
              <h2>From your first requirement to a working solution.</h2>
              <p>
                A clear process helps keep the project focused and makes
                decisions easier at every stage.
              </p>
            </div>

            <ol className="fit-about__process">
              {process.map((step, index) => (
                <li key={step.title}>
                  <span className="fit-about__step-number" aria-hidden="true">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* Technology */}
        <section className="fit-about__section">
          <div className="fit-about__container">
            <div className="fit-about__section-heading">
              <span className="fit-about__eyebrow">TECHNOLOGY</span>
              <h2>The right tools for the requirement.</h2>
              <p>
                We choose technologies around the application, integration
                needs, and long-term maintenance requirements.
              </p>
            </div>

            <div className="fit-about__technology-grid">
              {technologyGroups.map((group) => (
                <section key={group.title}>
                  <h3>{group.title}</h3>
                  <ul>
                    {group.technologies.map((technology) => (
                      <li key={technology}>{technology}</li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          </div>
        </section>

        {/* Company details and contact */}
        <section className="fit-about__section fit-about__section--soft">
          <div className="fit-about__container fit-about__closing">
            <div className="fit-about__company-details">
              <span className="fit-about__eyebrow">COMPANY DETAILS</span>
              <h2>Formax IT Solutions Private Limited</h2>
              <p>
                Ground Floor, 16, Mochpole, Near Junior School, Nabapally,
                Barasat, Kolkata – 700126, West Bengal, India
              </p>

              <dl className="fit-about__registration-grid">
                <div>
                  <dt>CIN</dt>
                  <dd>U72900WB2018PTC228240</dd>
                </div>
                <div>
                  <dt>GSTIN</dt>
                  <dd>19AADCF5125F1ZT</dd>
                </div>
              </dl>
            </div>

            <div className="fit-about__contact-card">
              <FaBuilding aria-hidden="true" />
              <h2>Let’s talk about what you need.</h2>
              <p>
                Share your project requirements, service enquiry, or partnership
                idea with our team.
              </p>
              <Link to="/contact-us" className="fit-about__button">
                Contact us
                <FaArrowRight aria-hidden="true" />
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default AboutUs;
